/**
 * CLI Token Reader Service (Browser-compatible)
 * 
 * This service reads CLI authentication from environment variables
 * and validates it with device fingerprint.
 */

import crypto from 'crypto-js';

export class CLITokenReader {
  private static instance: CLITokenReader;
  private cachedToken: string | null = null;
  private cachedAuthInfo: any = null;
  private lastCheck: number = 0;
  private readonly CACHE_DURATION = 30 * 1000; // 30 seconds
  private deviceValidated: boolean = false;
  private validationTimestamp: number = 0;
  private readonly VALIDATION_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  static getInstance(): CLITokenReader {
    if (!this.instance) {
      this.instance = new CLITokenReader();
    }
    return this.instance;
  }

  /**
   * Get device fingerprint in browser
   * This tries to match the CLI device fingerprint format: platform-hostname-username
   */
  private getDeviceFingerprint(): string {
    if (typeof window === 'undefined') {
      return '';
    }

    // Map navigator.platform to os.platform() equivalents
    let platform = 'unknown';
    const navPlatform = navigator.platform.toLowerCase();
    if (navPlatform.includes('mac')) {
      platform = 'darwin';
    } else if (navPlatform.includes('win')) {
      platform = 'win32';
    } else if (navPlatform.includes('linux')) {
      platform = 'linux';
    } else {
      platform = navPlatform;
    }
    
    // Use localhost for development, actual hostname for production
    const hostname = window.location.hostname || 'localhost';
    
    // Create a consistent username hash from user agent
    const userAgent = navigator.userAgent || 'unknown';
    const username = crypto.SHA256(userAgent).toString().substring(0, 8);
    
    return `${platform}-${hostname}-${username}`;
  }


  /**
   * Decrypt token with device fingerprint
   */
  private decryptToken(encryptedToken: string): any | null {
    try {
      // First check if this is a multi-token format (separated by |)
      const tokens = encryptedToken.includes('|') ? encryptedToken.split('|') : [encryptedToken];
      
      // Try each token
      for (const token of tokens) {
        const [ivHex, encryptedData, signature] = token.split('.');
        
        if (!ivHex || !encryptedData || !signature) {
          continue; // Try next token
        }

        // Get current device fingerprint
        const currentFingerprint = this.getDeviceFingerprint();
        const MOONUI_SECRET_KEY = 'moonui-2024-auth-encryption-key-v1';
        
        // Create key from secret + fingerprint
        const keyString = MOONUI_SECRET_KEY + currentFingerprint;
        const key = crypto.PBKDF2(keyString, 'moonui-salt-2025', {
          keySize: 256 / 32,
          iterations: 10000
        });

        // Verify signature (using only encryptedData, matching updated init.ts)
        const hmac = crypto.HmacSHA256(encryptedData, key);
        const computedSignature = hmac.toString();
        
        if (computedSignature === signature) {
          console.log('[MoonUI Pro] Signature verified with device fingerprint');
          
          // Decrypt data
          const iv = crypto.enc.Hex.parse(ivHex);
          const encrypted = crypto.enc.Hex.parse(encryptedData);
          
          const decrypted = crypto.AES.decrypt(
            { ciphertext: encrypted } as any,
            key,
            {
              iv: iv,
              mode: crypto.mode.CBC,
              padding: crypto.pad.Pkcs7
            }
          );

          const decryptedText = decrypted.toString(crypto.enc.Utf8);
          const tokenData = JSON.parse(decryptedText);
          
          // Verify device fingerprint matches
          if (tokenData.fingerprint) {
            const fingerprintParts = tokenData.fingerprint.split('-');
            const currentParts = currentFingerprint.split('-');
            
            // Check platform matches (OS must be same)
            if (fingerprintParts[0] !== currentParts[0]) {
              console.error('[MoonUI Pro] Platform mismatch - wrong OS');
              continue; // Try next token
            }
            
            console.log('[MoonUI Pro] Device validation successful');
          }
          
          console.log('[MoonUI Pro] Token decrypted successfully');
          return tokenData;
        }
      }
      
      console.error('[MoonUI Pro] Failed to decrypt token - no matching device fingerprint');
      return null;
      
    } catch (error) {
      console.error('[MoonUI Pro] Failed to decrypt token:', error);
      return null;
    }
  }

  /**
   * Try to get CLI token from environment variable
   */
  async getCLIToken(): Promise<string | null> {
    // Check cache first
    const now = Date.now();
    if (this.cachedToken && (now - this.lastCheck) < this.CACHE_DURATION) {
      return this.cachedToken;
    }

    // Check if we're in development by looking at hostname
    const isDevelopment = typeof window !== 'undefined' && 
                         (window.location.hostname === 'localhost' || 
                          window.location.hostname === '127.0.0.1' ||
                          window.location.hostname.includes('.local') ||
                          window.location.port === '3000' ||
                          window.location.port === '3001');
    
    // In development, try to get token from env
    if (isDevelopment) {
      const encryptedToken = process.env.NEXT_PUBLIC_MOONUI_DEV_TOKEN;
      
      if (encryptedToken) {
        const tokenData = this.decryptToken(encryptedToken);
        
        if (tokenData && tokenData.token) {
          // Skip strict fingerprint validation in development
          // The decryption itself validates the device
          console.log('[MoonUI Pro] Device fingerprint validation passed for development');
          
          // Additional server-side device validation for security (only once per day)
          const validationAge = now - this.validationTimestamp;
          if (!this.deviceValidated || validationAge > this.VALIDATION_CACHE_DURATION) {
            try {
              const isDeviceActive = await this.validateDeviceWithServer(tokenData.token);
              if (!isDeviceActive) {
                console.error('[MoonUI Pro] Device is no longer active on server - token revoked');
                return null;
              }
              
              // Cache successful validation
              this.deviceValidated = true;
              this.validationTimestamp = now;
              console.log('[MoonUI Pro] Device validation successful - cached for 24 hours');
            } catch (error) {
              // If validation fails, allow development to continue but warn
              console.warn('[MoonUI Pro] Could not validate device with server - using offline mode');
              // Don't update validation cache on network errors
            }
          }
          
          console.log('[MoonUI Pro] CLI authentication validated successfully');
          this.cachedToken = tokenData.token;
          this.cachedAuthInfo = {
            user: tokenData.user,
            plan: tokenData.plan
          };
          this.lastCheck = now;
          return tokenData.token;
        }
      } else {
        console.log('[MoonUI Pro] No development token found. Run: moonui init --auth');
      }
    }

    return null;
  }

  /**
   * Clear cached token
   */
  clearCache() {
    this.cachedToken = null;
    this.cachedAuthInfo = null;
    this.lastCheck = 0;
    this.deviceValidated = false;
    this.validationTimestamp = 0;
  }
  
  /**
   * Validate device with server (security check)
   */
  private async validateDeviceWithServer(token: string): Promise<boolean> {
    try {
      const currentFingerprint = this.getDeviceFingerprint();
      const [platform, hostname] = currentFingerprint.split('-');
      
      const response = await fetch('https://moonui.dev/api/device/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Device-ID': currentFingerprint,
          'X-Platform': platform,
          'X-Hostname': hostname
        },
        body: JSON.stringify({
          deviceId: currentFingerprint,
          type: 'development'
        })
      });

      if (!response.ok) {
        // 401 means device is revoked or not active
        if (response.status === 401) {
          return false;
        }
        // Other errors, assume device is still valid (offline mode)
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.valid === true;
    } catch (error) {
      // Network errors - allow development to continue
      throw error;
    }
  }

  /**
   * Get cached auth info (if available)
   */
  getCachedAuthInfo() {
    return this.cachedAuthInfo;
  }
}

export const cliTokenReader = CLITokenReader.getInstance();